import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { login, clearError } from '../../store/slices/authSlice';
import { companyService, CompanySettings } from '../../services/companyService';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
    
    // Fetch company settings for branding
    const fetchCompanySettings = async () => {
      try {
        const settings = await companyService.getCompanySettings();
        setCompanySettings(settings);
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
        // Set default fallback
        setCompanySettings({
          id: 'default',
          companyName: 'Purchase Request Management System',
          createdAt: '',
          updatedAt: '',
        });
      }
    };
    
    fetchCompanySettings();
  }, [dispatch]);

  useEffect(() => {
    // Redirect to dashboard if already authenticated
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== FORM SUBMIT ===');
    console.log('Email:', email);
    console.log('Password length:', password.length);
    
    if (!email || !password) {
      console.log('Missing email or password');
      return;
    }

    try {
      console.log('Dispatching login action...');
      const result = await dispatch(login({ email, password })).unwrap();
      console.log('Login dispatch successful:', result);
      // Navigation will happen automatically via useEffect when isAuthenticated becomes true
    } catch (error) {
      // Error is handled by Redux
      console.error('Login dispatch failed:', error);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Company Branding */}
          <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Avatar
              src={companySettings?.logoUrl && companySettings?.logoFilename ? 
                companyService.getLogoUrl(companySettings.logoFilename) : undefined}
              sx={{ 
                width: 80, 
                height: 80,
                bgcolor: 'primary.main'
              }}
            >
              <BusinessIcon sx={{ fontSize: 40 }} />
            </Avatar>
            
            <Typography component="h1" variant="h4" color="primary" align="center">
              {companySettings?.companyName || 'Purchase Request Management System'}
            </Typography>
          </Stack>
          
          <Typography component="h2" variant="h6" sx={{ mb: 3 }}>
            Sign In
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading || !email || !password}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 